import { useState } from "react";
import BtsPlayer from "../components/BtsPlayer";
import type { SeriesBts } from "./useBehindTheScenes";
import type { SeriesDetailsShow } from "./useSeriesDetails";
import { useTracking } from "@/components/ClientAnalytics";
import usePremium from "@/hooks/usePremium";
import { AmplitudeTrackingEvent } from "@/types/amplitude-tracking.type";

/**
 * What a click on a BTS tile does (SHO-757), shared by the section and its
 * View all page: the paywall for a locked one (`openPaywall`, the page's),
 * nothing for a VIP's that still reads locked, the player otherwise, with
 * `open bts` on the click and `finish bts` at the end. `player` is the
 * overlay to render, `null` when closed.
 */
export default function useBtsPlayback(
  show: SeriesDetailsShow | undefined,
  /** The page's; called with no episode number. */
  openPaywall: () => void,
) {
  const { trackAmplitudeEvent } = useTracking();
  const { isPremiumUser } = usePremium();
  const [playing, setPlaying] = useState<(SeriesBts & { url: string }) | null>(
    null,
  );

  const btsProperties = (bts: SeriesBts) => ({
    "series name": show?.title ?? "",
    "bts name": bts.title,
  });

  const onBtsClick = (bts: SeriesBts) => {
    // `url` is null exactly when locked; the type doesn't tie the two.
    if (bts.locked || !bts.url) {
      // A VIP still reading locked is the server catching up (the backoff
      // in useBehindTheScenes): nothing to sell, and the paywall would open
      // and close itself on `isPremiumUser`, a `show paywall` counted for
      // a payer. The tile is disabled then; this is the rule, not the UI.
      if (!isPremiumUser) openPaywall();
      return;
    }
    trackAmplitudeEvent(AmplitudeTrackingEvent.openBts, btsProperties(bts));
    setPlaying({ ...bts, url: bts.url });
  };

  const close = () => setPlaying(null);
  const player = playing ? (
    <BtsPlayer
      bts={playing}
      onClose={close}
      onEnded={() => {
        trackAmplitudeEvent(
          AmplitudeTrackingEvent.finishBts,
          btsProperties(playing),
        );
        close();
      }}
    />
  ) : null;

  return { onBtsClick, player };
}
